import { jwtDecode, JwtPayload } from "jwt-decode";
import axios, { endpoints } from "src/libs/axios";

interface CustomJwtPayload extends JwtPayload {
  role?: string;
}

export const isLoggedIn = () => {
  try {
    const storedData = localStorage.getItem("persist:user");
    if (!storedData) return false;

    const userData = JSON.parse(storedData);
    const token = userData?.token;

    if (!token || token === "null") return false;

    const decoded = jwtDecode<CustomJwtPayload>(token);
    if (decoded?.exp && Date.now() >= decoded.exp * 1000) {
      localStorage.removeItem("persist:user");
      return false;
    }

    return true;
  } catch (err) {
    console.error("Error checking login:", err);
    return false;
  }
};
export const getCountry = () => {
  const userData = JSON.parse(localStorage.getItem("persist:user") ?? "");
  if (userData?.token) {
    const country = JSON.parse(userData?.countryCode);
    return country;
  }
};

export const isPayOnDeliveryEnabled = async () => {
  try {
    const response = await axios.get(`${endpoints.users}/me`);
    const roleInput = response?.data?.data?.role;
    if (!roleInput) return false;
    const roles = Array.isArray(roleInput) ? roleInput : [roleInput];
    return roles.some((role) => String(role).includes("payment_later"));
  } catch (error) {
    console.error("Failed to fetch current user roles", error);
    return false;
  }
};

/** Normalize role strings for comparison (handles "Tax Exception", "tax-exception", etc.). */
function normalizeRoleKey(role: unknown): string {
  return String(role ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

function flattenRoles(roleInput: unknown): string[] {
  if (roleInput == null) return [];
  const arr = Array.isArray(roleInput) ? roleInput : [roleInput];
  return arr.map((r) => String(r ?? "").trim()).filter(Boolean);
}

function rolesIncludeTaxException(roles: string[]): boolean {
  return roles.some((r) => {
    const n = normalizeRoleKey(r);
    return n === "tax_exception" || n.includes("tax_exception");
  });
}

/** Backend `Role.TaxException` — no sales tax on checkout for this user. */
export function userHasTaxExceptionRole(
  userData: { role?: unknown } | null | undefined,
): boolean {
  const fromUser = flattenRoles(userData?.role);
  if (rolesIncludeTaxException(fromUser)) return true;

  // Login payload used to send only user.role[0]; JWT still carries full `role[]`.
  try {
    const raw = localStorage.getItem("persist:user");
    if (!raw) return false;
    const persisted = JSON.parse(raw) as { token?: string | null };
    const token = persisted?.token;
    if (!token || token === "null") return false;
    const decoded = jwtDecode<CustomJwtPayload & { role?: unknown }>(token);
    return rolesIncludeTaxException(flattenRoles(decoded?.role));
  } catch {
    return false;
  }
}

const MEMBERSHIP_LABEL_TO_TIER: Record<string, string> = {
  default: "TIER_0",
  thank_you: "TIER_1",
  thankyou: "TIER_1",
  launch: "TIER_2",
  move: "TIER_3",
  run: "TIER_4",
  accelerate: "TIER_5",
  take_off: "TIER_6",
  takeoff: "TIER_6",
};

/** API may return `TIER_3`, `{ tier, label }`, or admin label e.g. `"Move"`. */
export function normalizeMembershipTier(raw: unknown): string | undefined {
  if (raw == null) return undefined;

  if (typeof raw === "object") {
    const obj = raw as { tier?: unknown; label?: unknown };
    return (
      normalizeMembershipTier(obj.tier) ??
      normalizeMembershipTier(obj.label)
    );
  }

  const value = String(raw).trim();
  if (!value) return undefined;

  const upper = value.toUpperCase();
  if (/^TIER_\d+$/.test(upper)) return upper;

  const key = value.toLowerCase().replace(/[\s-]+/g, "_");
  return MEMBERSHIP_LABEL_TO_TIER[key];
}

export function prettifyNumber(number: number, locale = "en-US") {
  return new Intl.NumberFormat(locale).format(number);
}

/** Hybrid / screen-print products price by size + print type, not width × height. */
export function isScreenPrintTransferPricing(productConfig: {
  priceCalculationType?: string;
} | null | undefined): boolean {
  return (
    String(productConfig?.priceCalculationType || "")
      .trim()
      .toLowerCase() === "screen-print-transfer-pricing"
  );
}

/** Format price with exactly 2 decimal places (e.g. 0 → "0.00", 12.5 → "12.50"). */
export function formatPrice(value: number | undefined | null, locale = "en-US") {
  const num = Number(value);
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(isNaN(num) ? 0 : num);
}

/** Backend may return INR symbol (₹); normalize to "$" for cart UI. */
export function normalizeCartCurrencyCopy(message: string): string {
  if (!message || typeof message !== "string") return message;
  return message.replace(/\u20B9/g, "$");
}
